import { useEffect, useState } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/stores/auth'
import { useOrg } from '@/stores/org'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { HealthProbe } from '@/components/HealthProbe'
import { ReferralBind } from '@/components/ReferralBind'
import { Logo, LogoMark } from '@/components/Logo'
import { ORG_PICKER_EVENT } from '@/components/OrgGuard'
import { CommandPalette } from '@/components/CommandPalette'
import { ThemeToggle } from '@/components/ui/theme-toggle'
import { LanguageSwitch } from '@/components/LanguageSwitch'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuRow, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { formatApiError } from '@/lib/api'
import { useOrgQuery } from '@/lib/queries'
import { fetchInbox, notificationsKeys } from '@/lib/resources/notifications'
import { cn } from '@/lib/utils'
import { Bell, ChevronDown, LogOut } from 'lucide-react'

// 顶栏铃铛的轻量轮询 — 与 HealthProbe 的 30s 壳层节奏一致（constants.ts 不在本任务改动范围）。
const NOTIFICATIONS_POLL_MS = 30_000

export function TopBar() {
  const { t } = useTranslation()
  const tr = t as unknown as (k: string, o?: Record<string, unknown>) => string
  const { user, logout } = useAuth()
  const { orgs, activeOrgId, switchOrg, createOrg } = useOrg()
  const [newOrg, setNewOrg] = useState('')
  const [creating, setCreating] = useState(false)
  const [createErr, setCreateErr] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    if (!showCreate) return
    function onDown(e: MouseEvent) {
      const t2 = e.target as HTMLElement | null
      if (!t2?.closest('[data-create-popover]')) setShowCreate(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setShowCreate(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [showCreate])

  // 页面内 CTA（OrgGuard 空态）经 OrgGuard.openOrgPicker() 广播此事件请求打开组织入口：
  // membership 为空时 select 只有 disabled placeholder —— 直接展开创建组织表单；
  // 有可选组织时聚焦/展开 header org select 的兜底动作收在 OrgGuard.openOrgPicker() 内。
  useEffect(() => {
    function onOpenOrgPicker() {
      if (orgs.length === 0) {
        setCreateErr(null)
        setShowCreate(true)
      }
    }
    window.addEventListener(ORG_PICKER_EVENT, onOpenOrgPicker)
    return () => window.removeEventListener(ORG_PICKER_EVENT, onOpenOrgPicker)
  }, [orgs.length])

  // 顶栏铃铛的未读角标 — 用 status:'unread' 与 /notifications 的「未读」筛选同 key 同参数，
  // 缓存合一；角标数字读服务端 unreadTotal（read_at IS NULL 的组织级未读总数，
  // 不受 status/cursor 影响），不再用第一页的窗口估计；useOrgQuery 在无组织时不取数。
  const inboxQ = useOrgQuery(
    notificationsKeys.inbox('unread'),
    (orgId) => fetchInbox(orgId, { status: 'unread', limit: 50 }),
    { pollMs: NOTIFICATIONS_POLL_MS },
  )
  const unreadCount = inboxQ.data?.unreadTotal ?? 0

  // 手机注册用户的 email 是合成的 `${phone}@phone.local`(mock 与 module-auth 同一约定)—
  // 不当身份展示;优先显示姓名,否则把合成域名剥掉还原手机号。
  const userLabel =
    user?.name ||
    (user?.email?.endsWith('@phone.local') ? user.email.slice(0, -'@phone.local'.length) : user?.email)

  return (
      <header className="sticky top-0 z-40 flex min-h-[55px] items-center border-b border-enterprise-border bg-enterprise-bg">
        <div className="mx-auto flex w-full max-w-[1280px] items-center gap-2 sm:gap-3 px-4 sm:px-6 lg:gap-4 min-w-0 2xl:max-w-[1650px]">
          <Link
            to="/"
            className="flex shrink-0 items-center gap-3 min-w-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-enterprise-indigo focus-visible:ring-offset-2 focus-visible:ring-offset-enterprise-bg"
            aria-label={t('layout.backToHome')}
            title={t('layout.backToHomeTitle')}
          >
            {/* 窄屏用 1:1 mark（LogoMark 的既定用途），wordmark 151px 会把顶栏挤出视口；
                lg 叠 caption 时总高 ≤46px —— 55px 顶栏内保持标准 inset，不再顶到上边缘 */}
            <LogoMark className="block h-8 w-8 text-enterprise-text sm:hidden" />
            <span className="hidden min-w-0 sm:block">
              <Logo className="block h-8 w-auto text-enterprise-text" />
              <span className="mt-0.5 hidden whitespace-nowrap font-mono text-eyebrow uppercase leading-4 tracking-[0.16em] text-enterprise-muted lg:block">{t('layout.brandCaption')}</span>
            </span>
          </Link>
          <ReferralBind />
          <span className="ml-2 hidden shrink-0 lg:inline-flex">
            <HealthProbe />
          </span>

          <div className="ml-auto flex min-w-0 items-center gap-1 sm:gap-2">
            <span className="shrink-0">
              <CommandPalette />
            </span>
            <select
              value={activeOrgId ?? ''}
              onChange={(e) => switchOrg(e.target.value)}
              className={cn(
                // 唯一允许被压缩的顶栏控件：极窄屏下 select 缩到 basis 之下，其余保持 shrink-0。
                // [font-size:1rem] — 原生 select <16px 聚焦会触发 iOS 页面缩放（同 ui/input 的下限约定）
                'h-10 w-[104px] min-w-0 shrink truncate border border-enterprise-border bg-enterprise-surface pl-2 pr-7 font-mono text-sm [font-size:1rem] text-enterprise-text hover:border-enterprise-dim focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-enterprise-indigo focus-visible:ring-offset-2 focus-visible:ring-offset-enterprise-bg sm:w-auto sm:min-w-[150px] xl:min-w-[160px] 2xl:min-w-[170px]',
                // 无 membership 时 select 只剩 disabled placeholder —— 窄屏隐藏它给创建入口腾出视口内位置
                orgs.length === 0 && 'hidden sm:flex',
              )}
              aria-label={t('layout.selectOrg')}
              title={activeOrgId ?? undefined}
            >
              <option value="" disabled>
                {orgs.length ? t('layout.selectOrgPlaceholder') : t('layout.noOrg')}
              </option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id} title={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
            {/* membership 为空时移动端也露出创建入口 —— 否则手机端新用户无途径建组织 */}
            <div data-create-popover className={cn('relative shrink-0', orgs.length === 0 ? 'flex' : 'hidden sm:flex')}>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  if (!showCreate) setCreateErr(null)
                  setShowCreate((v) => !v)
                }}
                aria-expanded={showCreate}
                aria-label={t('layout.newOrg')}
                title={t('layout.newOrg')}
                className="h-10 w-10 shrink-0 border border-enterprise-border bg-enterprise-surface p-0 font-mono text-xs text-enterprise-muted hover:border-enterprise-dim hover:text-enterprise-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-enterprise-indigo focus-visible:ring-offset-2 focus-visible:ring-offset-enterprise-bg"
              >
                ＋
              </Button>
              {showCreate && (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault()
                    if (!newOrg.trim()) return
                    setCreateErr(null)
                    setCreating(true)
                    try {
                      await createOrg(newOrg.trim())
                      setNewOrg('')
                      setShowCreate(false)
                    } catch (err) {
                      setCreateErr(formatApiError(err, t('layout.createOrgFailed')))
                    } finally {
                      setCreating(false)
                    }
                  }}
                  className="absolute right-0 top-full z-50 mt-2 border border-enterprise-border bg-enterprise-bg p-2 shadow-lg"
                >
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder={t('layout.newOrgPlaceholder')}
                      value={newOrg}
                      onChange={(e) => setNewOrg(e.target.value)}
                      className="h-10 w-40 shrink-0 border-enterprise-border bg-enterprise-surface font-mono text-xs placeholder:text-enterprise-subtle hover:border-enterprise-dim focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-enterprise-indigo focus-visible:ring-offset-2 focus-visible:ring-offset-enterprise-bg focus-visible:border-enterprise-indigo"
                      aria-label={t('layout.newOrgNameAria')}
                      autoFocus
                    />
                    <Button
                      type="submit"
                      size="sm"
                      disabled={!newOrg.trim() || creating}
                      className="h-10 shrink-0 whitespace-nowrap border border-transparent bg-enterprise-indigo px-3 text-xs font-medium text-white hover:bg-enterprise-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-enterprise-indigo focus-visible:ring-offset-2 focus-visible:ring-offset-enterprise-bg"
                    >
                      {t('layout.create')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowCreate(false)}
                      aria-label={t('layout.close')}
                      className="h-10 w-10 shrink-0 border border-transparent p-0 text-xs text-enterprise-subtle hover:text-enterprise-text"
                    >
                      ✕
                    </Button>
                  </div>
                  {createErr ? (
                    <p role="alert" className="mt-1.5 max-w-56 text-xs text-enterprise-danger">
                      {createErr}
                    </p>
                  ) : null}
                </form>
              )}
            </div>
            {/* 通知铃铛 — /notifications 页的常驻入口；NavLink 命中时自带 aria-current="page"。
                shrink-0 + 圆形定宽，与 ＋/select 同一 40px 控件行，窄屏压缩仍由 org select 承担。 */}
            <NavLink
              to="/notifications"
              end
              aria-label={tr('layout.notificationsAria', { count: unreadCount })}
              title={tr('nav.notifications')}
              className={({ isActive }) =>
                cn(
                  'relative inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border motion-transition-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-enterprise-indigo focus-visible:ring-offset-2 focus-visible:ring-offset-enterprise-bg',
                  isActive
                    ? 'border-enterprise-indigo bg-enterprise-raised text-enterprise-indigo'
                    : 'border-enterprise-border bg-enterprise-surface text-enterprise-muted hover:border-enterprise-dim hover:bg-enterprise-raised hover:text-enterprise-text',
                )
              }
            >
              <Bell className="h-4 w-4" aria-hidden="true" />
              {unreadCount > 0 ? (
                <span
                  aria-hidden="true"
                  className="absolute -right-1 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-enterprise-indigo px-1 font-mono text-eyebrow font-bold tracking-normal tabular-nums text-enterprise-on-accent"
                >
                  {unreadCount}
                </span>
              ) : null}
            </NavLink>
            {/* 偏好开关上顶栏（同 landing 顶栏模式）：分段控件放菜单里太占块，
                compact 图标循环 + hidden sm 窄屏交给「更多」抽屉 */}
            <LanguageSwitch compact className="hidden shrink-0 sm:inline-flex" />
            <ThemeToggle compact className="hidden shrink-0 sm:inline-flex" />
            {/* 用户菜单 — 只留身份与退出 */}
            <DropdownMenu>
              <DropdownMenuTrigger
                aria-label={t('layout.userMenu.label')}
                title={user?.email ?? undefined}
                className="shrink-0 gap-2 px-2 font-mono text-xs"
              >
                <span aria-hidden="true" className="inline-flex h-5 w-5 shrink-0 items-center justify-center bg-enterprise-indigo font-mono text-eyebrow font-bold tracking-normal text-white">
                  {(userLabel ?? '?').slice(0, 1).toUpperCase()}
                </span>
                <span className="hidden max-w-[120px] truncate text-enterprise-text sm:inline">{userLabel}</span>
                <ChevronDown className="h-3.5 w-3.5 text-enterprise-subtle" aria-hidden="true" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="min-w-[240px]">
                <DropdownMenuRow className="flex-col items-start gap-0.5 px-3 py-2">
                  <span className="text-sm font-medium tracking-tight text-enterprise-text">{userLabel}</span>
                  {user?.email && user.name && !user.email.endsWith('@phone.local') ? (
                    <span className="max-w-[200px] truncate font-mono text-xs text-enterprise-subtle">{user.email}</span>
                  ) : null}
                </DropdownMenuRow>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={async () => {
                    await logout()
                    navigate('/login')
                  }}
                >
                  <LogOut className="h-4 w-4 text-enterprise-subtle" aria-hidden="true" />
                  {t('layout.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
  )
}
